import { FC, Fragment } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { Transition } from "@headlessui/react";
import { useSignal } from "@preact/signals";
import type { SlideTextProps } from "./interfaces";
import cn from "classnames";

const SlideText: FC<SlideTextProps> = ({
    children,
    className,
    textClassName,
    onClick,
}) => {
    const value = String(children);
    const oldText = useSignal("");
    const currentText = useSignal(value);
    const width = useSignal(0);
    const left = useSignal(0);
    const elementRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (value !== currentText.value) {
            oldText.value = currentText.value;

            if (elementRef.current) {
                width.value = elementRef.current.clientWidth;
                left.value = elementRef.current.offsetLeft;
            }
        }

        currentText.value = value;
    }, [currentText, left, oldText, value, width]);

    return (
        <div className={cn("relative", className)} onClick={onClick}>
            <Transition
                key={currentText.value}
                show
                appear
                enter="transition easy-in-out duration-200 origin-bottom-center"
                enterFrom="opacity-0 translate-y-4 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                as={Fragment}
            >
                <div ref={elementRef} className={textClassName}>
                    {currentText.value}
                </div>
            </Transition>
            {oldText.value !== currentText.value && (
                <div
                    key={oldText.value}
                    style={{
                        width: width.value ? width.value : undefined,
                        left: left.value,
                    }}
                    className={cn(
                        "origin-top-center pointer-events-none absolute top-0 animate-swipe-up",
                        textClassName,
                    )}
                    onAnimationEnd={() => {
                        oldText.value = currentText.peek();
                    }}
                >
                    {oldText.value}
                </div>
            )}
        </div>
    );
};

export default SlideText;
